<template>
  <div class="app-empty" :class="rootClasses">
    <div v-if="icon" class="app-empty__icon">
      <i :class="iconClass"></i>
    </div>

    <div class="app-empty__title">{{ title }}</div>

    <div v-if="description" class="app-empty__desc">
      {{ description }}
    </div>

    <button
      v-if="actionLabel"
      type="button"
      class="app-empty__action"
      @click="$emit('action')"
    >
      {{ actionLabel }}
    </button>

    <slot />
  </div>
</template>

<script>
/**
 * Empty-state / initial-hint card.
 *
 * Variants:
 *   - "default" — solid card (no-results state)
 *   - "hint"    — dashed-border card with no shadow (initial / instructional)
 *   - "inline"  — no card chrome; bare icon + title + desc (for inside tables)
 *
 * Props:
 *   icon         — FA class, e.g. "fa-magnifying-glass" (auto-prefixed with
 *                  weight `fa-regular`; pass full class string to override).
 *   title        — primary text.
 *   description  — secondary text (optional).
 *   actionLabel  — if set, renders a button that emits `action` event.
 */
export default {
  name: 'AppEmptyState',

  props: {
    icon:        { type: String, default: '' },
    title:       { type: String, required: true },
    description: { type: String, default: '' },
    actionLabel: { type: String, default: '' },
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'hint', 'inline'].includes(v),
    },
  },

  emits: ['action'],

  computed: {
    rootClasses() {
      return `app-empty--${this.variant}`
    },

    iconClass() {
      // Allow callers to pass either "fa-magnifying-glass" (we add weight)
      // or a fully-qualified "fa-solid fa-circle" (use as-is).
      if (this.icon.includes(' ')) return this.icon
      return `fa-regular ${this.icon}`
    },
  },
}
</script>

<style lang="scss" scoped>
$ae-text:        #2b3a4a;
$ae-text-muted:  #6c7a89;
$ae-text-subtle: #9aa6b3;
$ae-bg-surface:  #ffffff;
$ae-bg-soft:     #f6f8fa;
$ae-border:      rgba(0, 0, 0, 0.08);
$ae-blue:        #1f73c1;
$ae-blue-strong: #195aa0;
$ae-radius-md:   8px;
$ae-radius-lg:   12px;
$ae-shadow-sm:   0 1px 2px rgba(0, 0, 0, 0.04);
$ae-duration:    140ms;
$ae-ease:        cubic-bezier(.4, 0, .2, 1);

.app-empty {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;

  // ── Variants ──
  &--default {
    background: $ae-bg-surface;
    border: 0.5px solid $ae-border;
    border-radius: $ae-radius-lg;
    padding: 44px 24px;
    box-shadow: $ae-shadow-sm;
  }

  &--hint {
    background: $ae-bg-surface;
    border: 0.5px dashed $ae-border;
    border-radius: $ae-radius-lg;
    padding: 44px 24px;
  }

  &--inline {
    padding: 32px 16px;
  }

  // ── Icon ──
  &__icon {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    background: $ae-bg-soft;
    color: $ae-text-subtle;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    margin-bottom: 4px;
  }

  // ── Text ──
  &__title {
    font-size: 14px;
    font-weight: 600;
    color: $ae-text;
    line-height: 1.3;
  }

  &__desc {
    font-size: 12px;
    color: $ae-text-muted;
    max-width: 360px;
    line-height: 1.5;
  }

  // ── Action button (optional) ──
  &__action {
    margin-top: 8px;
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 500;
    background: $ae-blue;
    color: #fff;
    border: none;
    border-radius: $ae-radius-md;
    cursor: pointer;
    font-family: inherit;
    transition: background $ae-duration $ae-ease;

    &:hover { background: $ae-blue-strong; }

    &:focus-visible {
      outline: 2px solid $ae-blue;
      outline-offset: 2px;
    }
  }
}
</style>
